// PowerSync local-first dispatch for `deleteSpace` (#596 / ADR-015 — field
// delete of a room during capture, made offline-capable).
//
// Before this dispatch existed, deleteSpace was ONLINE-FIRST: the PowerSync
// adapter fell through to the REST transport, which DELETEd the room
// server-side synchronously. That left the confirm dialog stuck in its
// "deleting" state while offline (the REST adapter retries the DELETE forever
// in the background) and the room never disappeared. This makes the delete
// resolve immediately against local SQLite and propagate through the upload
// queue on reconnect — same local-first shape as cancelSession.
//
// Flow (the building-id read is a plain read; steps 2-4 run inside ONE write
// transaction — writeDeleteCascade — so the cascade is atomic and a crash or
// SQLite error mid-cascade can't tear the state; the ORDER inside the
// transaction still matters, see step 2):
//   1. Read the target room's building_id from the local mirror BEFORE it is
//      deleted, so the result envelope carries the right building reference.
//      Missing row → buildingId=null (idempotent: a duplicate confirm or a
//      room already gone locally; the connector's DELETE still acks).
//   2. INSERT a tombstone into pending_space_deletes (space_id, session_id).
//      THIS MUST HAPPEN BEFORE the spaces DELETE. The tombstone is how the
//      connector later distinguishes THIS user-intent delete (propagate to
//      the server) from the re-mint orphan-drop (local-only, never propagate)
//      — both produce a byte-identical spaces-DELETE CRUD entry carrying only
//      entry.id. It also carries the session_id the connector needs to build
//      the DELETE URL, which the deleted spaces row can no longer supply. The
//      table is localOnly, so this INSERT generates NO upload CRUD entry of
//      its own. The whole cascade (steps 2-4) commits in one transaction, so
//      there is no torn intermediate state to recover from; the tombstone-
//      first ORDER still encodes intent (tombstone present ⇒ user-delete, the
//      connector's discriminator) and is what the non-transactional fallback
//      in writeDeleteCascade preserves.
//   3. DELETE the room's capture_items locally (cascade). The server endpoint
//      hard-deletes them too (FK is onDelete: restrict, items-before-row); we
//      mirror that so the reactive room view drops the counts immediately and
//      a defensive consumer never sees items pointing at a now-gone room.
//   4. DELETE the spaces row. This is the write that generates the
//      spaces-DELETE CRUD entry the connector propagates to the server.
//   5. Return buildingStatus=null. The building-status recompute is a
//      server-side computation (it counts the building's REMAINING sessions);
//      it cannot be replicated faithfully offline, and inventing an optimistic
//      value risks drift. null matches the REST transport's 404 path
//      (capture-client-transport.ts) and dispatchCancelSession — downstream
//      (use-delete-room.ts → building-capture-content.tsx) already tolerates
//      null and falls back to the buildings sync stream for the authoritative
//      status. The optimistic room removal still happens (the local DELETE +
//      use-delete-room's deletedIds set).
//   6. Emit a `space-deleted` event with the optimistic shape so subscribers
//      reconcile on the next reactive tick.
//
// The connector (connector-dispatch-space-delete.ts, routed from connector.ts's
// spaces-DELETE branch) reads the tombstone, POSTs DELETE
// /capture/sessions/:sessionId/spaces/:spaceId, and clears the tombstone on
// success or 404 (idempotent). On a genuine 5xx it leaves the queued DELETE +
// the tombstone in place so the next cycle retries.

import type {
  CaptureMutation,
  CaptureMutationResult,
} from './capture-client.types'
import { eventForResult } from './capture-client-events'
import type { CaptureEventBus } from './capture-events'

import type { PowerSyncDb } from './capture-client-powersync'

interface SpaceLookupRow {
  buildingId: string | null
}

type ExecFn = (sql: string, params: unknown[]) => Promise<unknown>

/**
 * Run the local delete cascade atomically. With a transaction-capable db
 * (production's PowerSyncDatabase always is) the tombstone INSERT + item
 * cascade + spaces-row delete commit all-or-nothing, so a crash or SQLite
 * error mid-cascade can't strip a room's captured counts while leaving the
 * room behind with no spaces-DELETE queued to propagate the removal (Codex
 * P2 on #631). Falls back to sequential writes only for a db without
 * writeTransaction — unit mocks, never the field.
 */
async function writeDeleteCascade(
  db: PowerSyncDb,
  body: (exec: ExecFn) => Promise<void>,
): Promise<void> {
  if (db.writeTransaction) {
    await db.writeTransaction((tx) =>
      body((sql, params) => tx.execute(sql, params)),
    )
    return
  }
  await body((sql, params) => db.execute(sql, params))
}

export async function dispatchDeleteSpace(
  mutation: Extract<CaptureMutation, { kind: 'deleteSpace' }>,
  db: PowerSyncDb,
  bus: CaptureEventBus,
): Promise<CaptureMutationResult> {
  // 1. Read building_id off the local room before deleting it. Used only for
  // the result envelope (the connector recovers the session from the
  // tombstone, NOT from this). Missing local row → buildingId=null: the room
  // is already gone locally (duplicate confirm / concurrent removal); the
  // tombstone + DELETE below are still written so the connector acks/propagates
  // idempotently.
  const spaceRows = await db.getAll<SpaceLookupRow>(
    `SELECT building_id AS buildingId FROM spaces WHERE id = ? LIMIT 1`,
    [mutation.spaceId],
  )
  const buildingId = spaceRows[0]?.buildingId ?? null

  // 2-4. Tombstone + cascade + row delete, all inside ONE write transaction
  // so the cascade is all-or-nothing (Codex P2 on #631). The ORDER inside
  // still matters and is preserved.
  await writeDeleteCascade(db, async (exec) => {
    // 2. Tombstone FIRST (see header). localOnly ⇒ no upload CRUD entry.
    // Keyed by space_id (the only handle the connector has after the spaces
    // row is gone); carries session_id for the DELETE URL.
    await exec(
      `INSERT INTO pending_space_deletes (id, space_id, session_id)
       VALUES (?, ?, ?)`,
      [crypto.randomUUID(), mutation.spaceId, mutation.sessionId],
    )

    // 3. Cascade — capture_items first so a defensive consumer querying
    // mid-delete never sees orphan items pointing at a room that's already
    // gone. Mirrors the server endpoint's items-before-row delete order.
    await exec(`DELETE FROM capture_items WHERE space_id = ?`, [
      mutation.spaceId,
    ])

    // 4. DELETE the spaces row. Generates the spaces-DELETE CRUD entry the
    // connector reads the tombstone for and propagates.
    await exec(`DELETE FROM spaces WHERE id = ?`, [mutation.spaceId])
  })

  // 5. buildingStatus=null — server recomputes the authoritative value; the
  // buildings sync stream delivers it. Matches dispatchCancelSession + the
  // REST transport's 404 path.
  const result: CaptureMutationResult = {
    kind: 'deleteSpace',
    spaceId: mutation.spaceId,
    buildingId,
    buildingStatus: null,
  }
  bus.emit(eventForResult(mutation, result))
  return result
}
